import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import {
  Cake, ArrowRight, Loader2, MapPin, Calendar, Users, Lock,
  Sparkles, Camera,
} from "lucide-react";

/**
 * /invite/:token — PUBLIC teaser preview for a shared invite link.
 *
 * - No auth required.
 * - Shows ONLY the headline, date, location, host, and group name.
 * - Story, photos, bring list, and registry are gated behind sign-up.
 *
 * After the visitor signs up / logs in, they're routed through the
 * existing /app/join?token=... flow which actually adds them to the
 * group, then drops them on the event detail page.
 */
export default function InvitePreview() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/invite/preview/${token}`);
        setPreview(data);
        if (data?.title) {
          document.title = `You're invited: ${data.title} — Born Day`;
        }
      } catch (e) {
        setErr(
          e?.response?.data?.detail ||
            "This invite link looks broken or expired. Ask whoever shared it for a fresh link."
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [token, API]);

  const acceptPath = preview?.join_path
    ? `/login?next=${encodeURIComponent(preview.join_path)}`
    : `/register`;
  const registerPath = preview?.join_path
    ? `/register?next=${encodeURIComponent(preview.join_path)}`
    : `/register`;

  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--bg)", color: "var(--text)" }}
      data-testid="page-invite-preview"
    >
      {/* Title is set via document.title below so we don't ship the
          react-helmet-async dependency just for one tag. */}

      <header className="px-4 sm:px-6 py-4 flex items-center justify-between max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-2 font-extrabold text-xl" data-testid="invite-logo-home">
          <span
            className="w-9 h-9 grid place-items-center"
            style={{
              background: "var(--primary)",
              color: "var(--primary-fg)",
              borderRadius: "var(--radius-btn)",
              border: "1px solid var(--border)",
            }}
          >
            <Cake size={18} />
          </span>
          <span style={{ letterSpacing: "-0.04em" }}>Born Day</span>
        </Link>
        <Link to="/login" className="bd-btn bd-btn-ghost" data-testid="invite-already-have">
          I already have an account
        </Link>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        {loading && (
          <div className="text-center py-20">
            <Loader2 className="animate-spin mx-auto mb-3" />
            <p style={{ color: "var(--text-dim)" }}>Opening your invitation…</p>
          </div>
        )}

        {!loading && err && (
          <div className="bd-surface p-8 text-center" data-testid="invite-error">
            <h1 className="text-2xl font-bold mb-3">This invite isn't working.</h1>
            <p className="mb-6" style={{ color: "var(--text-dim)" }}>{err}</p>
            <Link to="/" className="bd-btn" data-testid="invite-error-home">
              Go to Born Day <ArrowRight size={14} />
            </Link>
          </div>
        )}

        {!loading && preview && (
          <>
            <div
              className="bd-chip mb-6"
              style={{ borderColor: "var(--primary)", color: "var(--primary)" }}
              data-testid="invite-eyebrow"
            >
              <Sparkles size={12} /> You're invited
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.98] mb-4"
              style={{ letterSpacing: "-0.05em" }}
              data-testid="invite-title"
            >
              {preview.title}
            </h1>

            <p
              className="text-lg mb-8"
              style={{ color: "var(--text-dim)" }}
              data-testid="invite-subtitle"
            >
              <strong style={{ color: "var(--text)" }}>{preview.host_name}</strong>
              {" "}invited you to{" "}
              {preview.kind === "event"
                ? <>this event {preview.group_name && <>in <strong style={{ color: "var(--text)" }}>{preview.group_name}</strong></>}</>
                : <>this group</>
              }{" "}on Born Day.
            </p>

            {/* Core meta — date, location, member count */}
            <div
              className="bd-surface p-5 sm:p-6 mb-8 space-y-3"
              style={{ borderRadius: "var(--radius-card)" }}
              data-testid="invite-meta"
            >
              {preview.date && (
                <div className="flex items-center gap-3" data-testid="invite-date">
                  <span
                    className="w-9 h-9 grid place-items-center shrink-0"
                    style={{
                      background: "var(--bg-soft)",
                      border: "1px solid var(--border)",
                      borderRadius: "var(--radius-btn)",
                    }}
                  >
                    <Calendar size={16} />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>
                      When
                    </div>
                    <div className="font-bold text-lg">{preview.date}</div>
                  </div>
                </div>
              )}
              {preview.location && (
                <div className="flex items-center gap-3" data-testid="invite-location">
                  <span
                    className="w-9 h-9 grid place-items-center shrink-0"
                    style={{
                      background: "var(--bg-soft)",
                      border: "1px solid var(--border)",
                      borderRadius: "var(--radius-btn)",
                    }}
                  >
                    <MapPin size={16} />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>
                      Where
                    </div>
                    <div className="font-bold text-lg">{preview.location}</div>
                  </div>
                </div>
              )}
              {preview.member_count != null && (
                <div className="flex items-center gap-3" data-testid="invite-members">
                  <span
                    className="w-9 h-9 grid place-items-center shrink-0"
                    style={{
                      background: "var(--bg-soft)",
                      border: "1px solid var(--border)",
                      borderRadius: "var(--radius-btn)",
                    }}
                  >
                    <Users size={16} />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-wider" style={{ color: "var(--text-dim)" }}>
                      Members
                    </div>
                    <div className="font-bold text-lg">
                      {preview.member_count} already in the circle
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Locked content teaser */}
            <div
              className="p-5 mb-8 flex items-start gap-3"
              style={{
                background: "var(--bg-soft)",
                border: "1px dashed var(--border)",
                borderRadius: "var(--radius-card)",
              }}
              data-testid="invite-locked"
            >
              <Lock size={18} className="shrink-0 mt-0.5" style={{ color: "var(--primary)" }} />
              <div>
                <div className="font-bold mb-1">More inside, just for guests.</div>
                <p className="text-sm" style={{ color: "var(--text-dim)" }}>
                  Story, photos, the Bring List, gift registry, RSVPs, and the rest
                  of the details unlock the moment you join. Free, takes 30 seconds.
                </p>
                <ul className="mt-2 text-sm flex flex-wrap gap-x-4 gap-y-1" style={{ color: "var(--text-dim)" }}>
                  <li className="flex items-center gap-1"><Camera size={12} /> Photos & videos</li>
                  <li>· Full story</li>
                  <li>· Bring list</li>
                  <li>· Registry</li>
                  <li>· RSVPs</li>
                </ul>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3">
              <Link
                to={registerPath}
                className="bd-btn"
                style={{ fontSize: "1rem", padding: "0.9rem 1.4rem" }}
                data-testid="invite-cta-accept"
              >
                Accept & sign up free <ArrowRight size={16} />
              </Link>
              <Link
                to={acceptPath}
                className="bd-btn bd-btn-ghost"
                style={{ fontSize: "1rem", padding: "0.9rem 1.4rem" }}
                data-testid="invite-cta-signin"
              >
                I already have an account
              </Link>
            </div>

            <p className="text-xs mt-6" style={{ color: "var(--text-dim)" }}>
              Born Day is a friendly social calendar for families and friends. No spam, no ads, free to use.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
